// The phone apps' store listings (iOS went live on the App Store 2026-11-02,
// docs/mobile-adoption-plan.md). One place for the links the /downloads page,
// the iPhone Smart App Banner (gatsby-ssr), the Android banner
// (components/AndroidAppBanner.tsx), and the homepage's phone buttons
// (components/PhoneStoreCta.tsx) use.

export const IOS_APP_ID = "6779744977";
export const IOS_APP_STORE_URL = `https://apps.apple.com/app/id${IOS_APP_ID}`;
export const ANDROID_PACKAGE = "so.eden.mobile";
export const ANDROID_PLAY_URL = `https://play.google.com/store/apps/details?id=${ANDROID_PACKAGE}`;

/**
 * An Android phone's own browser: Android with "Mobile" in the user agent
 * (tablets leave it out). Skips the Android WebView ("; wv)"), which is how
 * other apps, our own included, show web pages.
 */
export function isAndroidPhoneBrowser(userAgent: string): boolean {
  return /Android/i.test(userAgent) && /Mobile/i.test(userAgent) && !/; wv\)/.test(userAgent);
}

/** Safari or another browser on an iPhone. iPads and computers stay on web sign-up. */
export function isIPhoneBrowser(userAgent: string): boolean {
  return /iPhone|iPod/i.test(userAgent);
}

export type PhoneStore = { platform: "ios" | "android"; href: string };

/**
 * Whether the homepage sends iPhones to the App Store. Turn it off whenever
 * the App Store app can't sell the web's offer prices (it was off until
 * Apple approved the $5 Discover subscriptions, Dan 2026-11-03); iPhones
 * then sign up on the web with "Try risk-free". Android always goes to
 * Google Play.
 */
export const IOS_HOMEPAGE_STORE_CTA = true;

/** A `?via=` referral this browser carries (attribution.ts), for the store links. */
export type StoreReferral = { code: string; tag?: string | null };

/**
 * The Play link. Its install referrer tags where the tap came from (Play
 * Console shows it) and carries a referral code, which the Android app reads
 * on first open and sends with its first bootstrap, so the referrer gets the
 * signup (mobile/android core/install/InstallReferrerHandoff.kt).
 */
export function androidPlayUrl(medium: string, referral?: StoreReferral | null): string {
  const referrer = new URLSearchParams({ utm_source: "eden.so", utm_medium: medium });
  if (referral) {
    referrer.set("eden_via", referral.code);
    if (referral.tag) referrer.set("eden_tag", referral.tag);
  }
  return `${ANDROID_PLAY_URL}&referrer=${encodeURIComponent(referrer.toString())}`;
}

/**
 * Where a phone's "Get the app" button goes, or null (web sign-up).
 *
 * An iPhone that came from a referral link signs up on the web instead:
 * Apple carries nothing through an App Store install, so the web signup is
 * what credits the referrer. The welcome flow then ends on "Get the app"
 * (apps/ui/website welcome/steps/GetTheAppStep.tsx) and the app opens
 * signed in to that account.
 */
export function phoneStoreFor(
  userAgent: string,
  referral?: StoreReferral | null,
): PhoneStore | null {
  if (isIPhoneBrowser(userAgent)) {
    if (!IOS_HOMEPAGE_STORE_CTA || referral) return null;
    return { platform: "ios", href: IOS_APP_STORE_URL };
  }
  if (isAndroidPhoneBrowser(userAgent)) {
    return { platform: "android", href: androidPlayUrl("homepage", referral) };
  }
  return null;
}

/**
 * The homepage, where every button already says "Get the app" on a phone
 * (components/PhoneStoreCta.tsx). The store banners stay off here so a phone
 * doesn't see three "get" buttons at once. "/home-paths/" is the homepage's
 * other split-test arm (homeArm.ts).
 */
export function isHomepagePath(pathname: string): boolean {
  return pathname === "/" || pathname === "/home-paths" || pathname === "/home-paths/";
}
